// Meta Pixel provider — dynamically injects fbevents.js and wraps fbq calls.
// To add a new standard-event mapping, add an entry to META_EVENT_MAP below.
//
// How to add your Pixel ID:
//   Set REACT_APP_META_PIXEL_ID=<your_id> in .env

declare global {
  interface Window {
    fbq: ((...args: any[]) => void) & {
      callMethod?: (...args: any[]) => void;
      queue: any[];
      loaded: boolean;
      version: string;
      push: (...args: any[]) => void;
    };
    _fbq: typeof window.fbq;
  }
}

// Maps our standard event names to Meta Pixel standard events.
// Events not listed here are sent as custom events via fbq('trackCustom', ...).
// Maps our event name prefixes/exact names → Meta Pixel standard events.
// Prefix matching (startsWith) is checked first, then exact match.
const META_PREFIX_MAP: Array<[string, string]> = [
  ["form_submit_",  "Lead"],              // form_submit_diagnose → Lead
  ["paymentLink_",  "InitiateCheckout"],  // paymentLink_check → InitiateCheckout
];

const META_EXACT_MAP: Record<string, string> = {
  purchase:     "Purchase",
  lead:         "Lead",
  page_view:    "PageView",
  view_content: "ViewContent",
  sign_up:      "CompleteRegistration",
};

const PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID ?? "";

function fbq(...args: any[]) {
  if (!window.fbq) return;
  if (window.fbq.callMethod) {
    window.fbq.callMethod(...args);
  } else {
    window.fbq.queue.push(args);
  }
}

export const metaPixel = {
  init() {
    if (!PIXEL_ID) return;

    // Set up the fbq stub so calls before the script loads are queued
    if (!window.fbq) {
      const n: any = function (...args: any[]) {
        n.callMethod ? n.callMethod(...args) : n.queue.push(args);
      };
      n.push = n;
      n.loaded = true;
      n.version = "2.0";
      n.queue = [];
      window.fbq = n;
      window._fbq = n;
    }

    // Dynamically inject the fbevents.js script
    if (!document.getElementById("meta-pixel-script")) {
      const script = document.createElement("script");
      script.id = "meta-pixel-script";
      script.async = true;
      script.src = "https://connect.facebook.net/en_US/fbevents.js";
      document.head.appendChild(script);
    }

    fbq("init", PIXEL_ID);
  },

  page(path: string) {
    if (!PIXEL_ID) return;
    // Standard PageView (Meta auto-captures full URL including hash).
    // content_name lets you filter by page in Meta Events Manager.
    fbq("track", "PageView", { content_name: path });
  },

  track(name: string, params?: Record<string, unknown>) {
    if (!PIXEL_ID) return;
    const prefixMatch = META_PREFIX_MAP.find(([prefix]) => name.startsWith(prefix));
    const metaEvent = prefixMatch?.[1] ?? META_EXACT_MAP[name];
    if (metaEvent) {
      fbq("track", metaEvent, params ?? {});
    } else {
      fbq("trackCustom", name, params ?? {});
    }
  },
};
